import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Label } from "recharts";
import UseGetDashBoardData from "../../../../../Components/Hooks/useDashboardData";
import colorCodes from "../../../../../Components/Utils/ColorCode";
import { useNavigate } from "react-router-dom";

const Industry = () => {
  const { data, loading } = UseGetDashBoardData(`/dashboard/customers-by-industry`);
  const [chartData, setChartData] = useState([]);
  const [sumValue, setSumValue] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (data?.customers_by_industry) {
      const formattedData = data.customers_by_industry.map((item) => ({
        name: item.industry || "Other",
        value: item.count || 0,
      }));
      formattedData.sort((a, b) => b.value - a.value);
      setChartData(formattedData);
      const sum = formattedData.reduce((acc, curr) => acc + curr?.value, 0);
      setSumValue(sum);
    }
  }, [data]);

  const handleSliceClick = (entry) => {
    const path = "/sales/customers";
    navigate(path, { state: { industry: entry.name } });
  };

  return (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_25px_rgba(0,0,0,0.04)] transition-all duration-300 h-full flex flex-col justify-between">
      <div className="flex justify-between items-center pb-3 border-b border-gray-100 mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">Customers by Industry</h3>
        </div>

        <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200/50">
          {sumValue} Industries
        </span>
      </div>

      {loading ? (
        <div className="h-52 flex items-center justify-center animate-pulse">
          <div className="h-36 bg-gray-100 rounded-xl w-full"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center flex-1">
          {/* Donut Chart */}
          <div className="md:col-span-5 relative flex justify-center items-center h-44">
            {sumValue > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={38}
                    outerRadius={58}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                    style={{ cursor: "pointer" }}
                    onClick={(entry) => handleSliceClick(entry)}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={colorCodes[index % colorCodes.length]}
                      />
                    ))}
                    <Label
                      value={`${sumValue}`}
                      position="center"
                      style={{ fontSize: "15px", fontWeight: "bold", fill: "#1F2937" }}
                    />
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1F2937",
                      borderRadius: "8px",
                      color: "#FFF",
                      border: "none",
                      fontSize: "11px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-gray-400 text-xs font-medium">No Industry Data</div>
            )}
          </div>

          {/* Industry List */}
          <div className="md:col-span-7 max-h-48 overflow-y-auto pr-1 space-y-2">
            {chartData.map((item, index) => {
              const color = colorCodes[index % colorCodes.length];
              const percent = sumValue > 0 ? Math.round((item.value / sumValue) * 100) : 0;
              return (
                <div
                  key={index}
                  onClick={() => handleSliceClick(item)}
                  className="p-2 rounded-xl bg-gray-50/70 hover:bg-gray-100/80 cursor-pointer transition-all duration-200"
                >
                  <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center space-x-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }}></span>
                      <span className="text-xs font-semibold text-gray-700 truncate max-w-[120px]">
                        {item.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-bold text-gray-900">{item.value}</span>
                      <span className="text-[10px] text-gray-400 font-medium">{percent}%</span>
                    </div>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200/60 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${percent}%`, backgroundColor: color }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Industry;
